import { Loader2, Upload, Trash, GripVertical, Files, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Form,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDropzone } from "react-dropzone";
import { useState } from "react";
import { toast } from "sonner";
import { image2pdf, mergePdfs } from "@/lib/utils";
import { z } from "zod";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const SortableItem = ({
  file,
  index,
  onRemove,
}: {
  file: File;
  index: number;
  onRemove: () => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `${file.name}-${index}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style} className="hover:bg-accent/5">
      <TableCell className="w-[50px]">
        <div {...attributes} {...listeners} className="cursor-grab">
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
      </TableCell>
      <TableCell className="w-[50px] font-medium">{index + 1}</TableCell>
      <TableCell className="w-full truncate">{file.name.slice(0, 36)} {file.name.length > 36 && "..."}</TableCell>
      <TableCell className="text-right w-min">
        {(file.size / (1000 * 1000)).toFixed(2)} mb
      </TableCell>
      <TableCell className="text-right w-min">
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="destructive"
            size="icon"
            className="size-8"
            onClick={onRemove}
          >
            <Trash className="size-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const MergePdfsForm = z.object({
  files: z.array(z.instanceof(File)),
});

type MergePdfsForm = z.infer<typeof MergePdfsForm>;


const MergePdfsPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isConverting, setIsConverting] = useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg']
    },
    multiple: true,
    onDrop: async (acceptedFiles) => {
      setIsConverting(true);
      try {
        const convertedFiles = await image2pdf(acceptedFiles);

        form.setValue("files", [
          ...form.getValues("files"),
          ...convertedFiles
        ]);
      } catch (error) {
        console.error('Error converting files:', error);
        toast.error('Error al convertir los archivos', { description: error instanceof Error ? error.message : 'Error desconocido' });
      } finally {
        setIsConverting(false);
      }
    }
  });

  // Additional dropzone for adding more files when some are already present
  const { getInputProps: getAdditionalInputProps, open: openFileDialog } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg']
    },
    multiple: true,
    noClick: true,
    noKeyboard: true,
    onDrop: async (acceptedFiles) => {
      setIsConverting(true);
      try {
        const convertedFiles = await image2pdf(acceptedFiles);

        form.setValue("files", [
          ...form.getValues("files"),
          ...convertedFiles
        ]);
      } catch (error) {
        console.error('Error converting files:', error);
        toast.error('Error al convertir los archivos', { description: error instanceof Error ? error.message : 'Error desconocido' });
      } finally {
        setIsConverting(false);
      }
    }
  });

  const form = useForm<MergePdfsForm>({
    resolver: zodResolver(MergePdfsForm),
    defaultValues: {
      files: [],
    },
  });

  // Set up sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end event
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const files = form.getValues("files");
      const oldIndex = parseInt(active.id.toString().split("-").pop() || "0");
      const newIndex = parseInt(over.id.toString().split("-").pop() || "0");

      form.setValue("files", arrayMove(files, oldIndex, newIndex));
    }
  };

  return (
    <div className="grid gap-3 w-full">
      <div className="flex justify-between items-end gap-6">
        <div className="flex flex-col">
          <h1 className="text-lg font-semibold">Unir Pdfs</h1>
          <p className="text-xs text-muted-foreground">
            Suba múltiples archivos PDF y unalos en un solo documento
          </p>
        </div>
      </div>
      <Separator />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(async (values) => {
            setIsLoading(true);
            const url = await mergePdfs(values.files);
            setIsLoading(false);
            window.open(url, "_blank");
            URL.revokeObjectURL(url);
          })}
          className="grid gap-3"
        >
          <FormField
            control={form.control}
            name="files"
            render={() => (
              <FormItem>
                <FormControl>
                  {form.watch("files").length === 0 && (
                    <div
                      {...getRootProps()}
                      className="w-full h-[250px] border-2 border-dashed rounded-md bg-accent/10 border-accent cursor-pointer"
                    >
                      <input {...getInputProps()} />
                      <div className="flex flex-col items-center justify-center h-full text-center w-full">
                        {isConverting ? (
                          <>
                            <div className="flex items-center gap-2 mb-6">
                              <Loader2 className="size-10 text-primary/60 animate-spin" />
                            </div>
                            <p className="text-sm font-medium text-foreground/80 mb-2">
                              Convirtiendo archivos...
                            </p>
                            <p className="text-xs text-muted-foreground/80">
                              Se están procesando las imágenes y PDFs
                            </p>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-2 mb-6">
                              <Files className="size-10 text-primary/60" />
                              <p className="text-4xl font-bold text-primary/60">Unir Pdfs</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Upload className="size-4 text-foreground/80" />
                              <p className="text-sm font-medium text-foreground/80">
                                {isDragActive
                                  ? "Suelte los archivos aquí"
                                  : "Arrastre los archivos aquí o haga clic para seleccionar"}
                              </p>
                            </div>
                            <p className="text-xs text-muted-foreground/80">
                              Múltiples archivos (PDF, PNG, JPG)
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {form.watch("files").length > 0 && (
            <div className="grid gap-3">
              <div className="flex justify-between items-center">
                <Button
                  type="button"
                  variant="success"
                  onClick={openFileDialog}
                  disabled={isConverting}
                  className="ml-auto"
                >
                  {isConverting ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <Upload className="size-4" />
                      Agregar
                    </>
                  )}
                </Button>
                <input {...getAdditionalInputProps()} style={{ display: 'none' }} />
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-accent/5">
                    <TableHead className="w-[50px]"></TableHead>
                    <TableHead className="w-[50px]">Orden</TableHead>
                    <TableHead className="w-full">Nombre</TableHead>
                    <TableHead className="text-right w-min">Tamaño</TableHead>
                    <TableHead className="text-right w-min">
                      Acciones
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={form
                      .watch("files")
                      .map((file, index) => `${file.name}-${index}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    <TableBody>
                      {form.watch("files").map((file, index) => (
                        <SortableItem
                          key={`${file.name}-${index}`}
                          file={file}
                          index={index}
                          onRemove={() => {
                            const updatedFiles = [...form.getValues("files")];
                            updatedFiles.splice(index, 1);
                            form.setValue("files", updatedFiles);
                          }}
                        />
                      ))}
                    </TableBody>
                  </SortableContext>
                </DndContext>
              </Table>
            </div>
          )}

          <div className="flex justify-between">
            <div className="flex gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Button
                      variant="destructive"
                      type="button"
                      disabled={isLoading || form.watch("files").length === 0}
                      className="gap-2"
                      onClick={() => {
                        form.reset();
                      }}
                    >
                      <RotateCcw className="size-4" />
                      Resetear
                    </Button>
                  </TooltipTrigger>
                  {(isLoading || form.watch("files").length === 0) && <TooltipContent>
                    <p>Agregue archivos para poder resetear</p>
                  </TooltipContent>}
                </Tooltip>
              </TooltipProvider>
            </div>
            <div>
              <TooltipProvider>
                <Tooltip disableHoverableContent={isLoading || form.watch("files").length < 1}>
                  <TooltipTrigger >
                    <Button
                      type="submit"
                      disabled={isLoading || form.watch("files").length < 1}
                      className="w-32 gap-2"
                    >
                      {isLoading  ? (
                        <>
                          <Loader2 className="size-4 animate-spin" />
                          Procesando...
                        </>
                      ) : (
                        <><Files className="size-4" /> Unir</>
                      )}
                    </Button>
                  </TooltipTrigger>
                  {(isLoading || form.watch("files").length < 1) && <TooltipContent>
                    <p>Agregue archivos para poder unir PDFs</p>
                  </TooltipContent>}
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </form>
        </Form>
    </div>
  );
};

export default MergePdfsPage;
